import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { CircularProgress, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import Uppy from '@uppy/core';
import { UppyContextProvider } from '@uppy/react';
import XHR from '@uppy/xhr-upload';
import sx from 'classnames';
import { GlobalWorkerOptions } from 'pdfjs-dist';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';

import { AttachmentFilePreviewer } from './components/AttachmentFilePreviewer';
import styles from './ExpenseFileAttachments.module.scss';
import { FileAttachmentModel } from './types';
import { ExpenseFormType } from '../../types';
import { useGetBlobFilesFromStorage } from '@/api/REST/blobFile';
import {
  EDIT_EXPENSE_ATTACHMENT_UPLOAD_ENDPOINT,
  EXPENSE_ATTACHMENTS_KEY,
  NEW_EXPENSE_ATTACHMENT_UPLOAD_ENDPOINT,
  useGetExpenseAttachments,
} from '@/api/REST/expenseAttachment';
import { FileUploadDropzone } from '@/components/ui/FileUploadDropzone';
import { useNotification } from '@/hooks/useNotification';

export const SUPPORTED_FILE_ATTACHMENT_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'image/bmp',
] as const;

/*
 * initialize PDF.js worker for PDF conversion to img for file previewing,
 * file gets static path by copying it to /public from /node_modules at dev start/ build time to prevent Vite path hashing issues
 */
GlobalWorkerOptions.workerSrc = '/pdf.worker.js';

type ExpenseFileAttachmentsProps =
  | {
      expenseType: ExpenseFormType;
      expenseId?: never;
      isExpenseLocked?: never;
      isExpenseLoading?: never;
      onAttachedExpenseFilesChanged: (attachedFiles: string[]) => void;
      onFilesUploading: (isUploading: boolean) => void;
    }
  | {
      expenseType: ExpenseFormType;
      expenseId: number | null;
      isExpenseLocked: boolean;
      isExpenseLoading: boolean;
      onAttachedExpenseFilesChanged?: never;
      onFilesUploading: (isUploading: boolean) => void;
    };

export type ExpenseFileAttachmentsHandle = {
  reset: () => void;
};

export const ExpenseFileAttachments = forwardRef<
  ExpenseFileAttachmentsHandle,
  ExpenseFileAttachmentsProps
>(
  (
    {
      expenseType,
      expenseId,
      isExpenseLocked = false,
      isExpenseLoading,
      onAttachedExpenseFilesChanged,
      onFilesUploading,
    }: ExpenseFileAttachmentsProps,
    ref,
  ) => {
    useImperativeHandle(ref, () => ({
      reset: () => {
        setFileAttachments([]);
        filesUploadedRef.current = false;
      },
    }));

    const { user } = useAuth();
    const { showError, showSuccess } = useNotification();
    const queryClient = useQueryClient();
    const { t } = useTranslation('expenses');
    const [fileAttachments, setFileAttachments] = useState<
      FileAttachmentModel[]
    >([]);

    // Track if any files were uploaded during this session
    const filesUploadedRef = useRef(false);

    // needed to have the latest expenseId in unmount cleanup function
    const expenseIdRef = useRef(expenseId);
    useEffect(() => {
      expenseIdRef.current = expenseId;
    }, [expenseId]);

    const {
      data: existingExpenseFileUrls,
      isLoading: isExpenseFilesUrlsLoading,
      isRefetching: isExpenseFilesUrlsRefetching,
    } = useGetExpenseAttachments(expenseType, expenseId ?? null);

    const {
      data: existingExpenseFilesBlobs,
      isLoading: isExistingFileBlobsLoading,
    } = useGetBlobFilesFromStorage(
      existingExpenseFileUrls ?? {},
      !!existingExpenseFileUrls &&
        !fileAttachments.length &&
        !isExpenseFilesUrlsRefetching,
    );

    const getExpenseAttachmentUploadEndpoint = () => {
      if (!expenseId) {
        return NEW_EXPENSE_ATTACHMENT_UPLOAD_ENDPOINT[expenseType];
      } else {
        return EDIT_EXPENSE_ATTACHMENT_UPLOAD_ENDPOINT[expenseType].replace(
          '{expenseId}',
          expenseId.toString(),
        );
      }
    };

    useEffect(() => {
      if (
        existingExpenseFileUrls &&
        !isExpenseFilesUrlsLoading &&
        !isExistingFileBlobsLoading
      ) {
        if (fileAttachments.length === 0) {
          const initialFileAttachments: FileAttachmentModel[] = Object.entries(
            existingExpenseFileUrls,
          ).map(([databaseUrl, signedUrl]) => {
            const blob = existingExpenseFilesBlobs?.find(
              (blob) => blob && Object.keys(blob)[0] === databaseUrl,
            )?.[databaseUrl];

            if (!blob) {
              return {
                file: null,
                isError: true,
                isUploaded: true,
                databaseUrl,
                signedUrl,
              };
            }

            const filenameFromUrl = databaseUrl?.split('/').pop() || 'download';

            const fileFromBlob = new File([blob], filenameFromUrl, {
              type: blob.type,
              lastModified: Date.now(),
            });

            return {
              file: fileFromBlob,
              isError: false,
              isUploaded: true,
              databaseUrl,
              signedUrl,
            };
          });

          setFileAttachments(initialFileAttachments);
        } else {
          setFileAttachments((currentFileAttachments) => {
            if (currentFileAttachments.length === 0) {
              return currentFileAttachments;
            }

            const updatedExpenseFileUrls = Object.entries(
              existingExpenseFileUrls,
            );

            return currentFileAttachments.map(
              (fileAttachment): FileAttachmentModel => {
                // Only update uploaded files that have a databaseUrl
                if (!fileAttachment.isUploaded || !fileAttachment.databaseUrl) {
                  return fileAttachment;
                }

                const match = updatedExpenseFileUrls.find(
                  ([databaseUrl, _signedUrl]) =>
                    databaseUrl === fileAttachment.databaseUrl,
                );

                if (!match) {
                  // If no match found, keep the existing attachment unchanged
                  // This preserves all properties to avoid triggering unnecessary re-renders
                  return fileAttachment;
                }

                // Only update the signedUrl, preserve all other properties
                return {
                  ...fileAttachment,
                  signedUrl: match[1], // Update with fresh signedUrl
                };
              },
            );
          });
        }
      }
    }, [
      existingExpenseFileUrls,
      isExpenseFilesUrlsLoading,
      existingExpenseFilesBlobs,
      isExistingFileBlobsLoading,
    ]);

    const [uppy] = useState(() => {
      const uppyInstance = new Uppy({
        restrictions: {
          allowedFileTypes: [...SUPPORTED_FILE_ATTACHMENT_TYPES],
          maxFileSize: 10 * 1024 * 1024, // 10 MB
          minNumberOfFiles: 1,
          maxNumberOfFiles: null,
        },
        autoProceed: true,
      }).use(XHR, {
        endpoint: getExpenseAttachmentUploadEndpoint(), // Initial endpoint
        headers: () => ({
          Authorization: user?.access_token
            ? `Bearer ${user.access_token}`
            : '',
        }), // use arrow function to always get the current token
      });
      uppyInstance.on('upload-start', () => {
        onFilesUploading(true);
      });
      uppyInstance.on('complete', (result) => {
        onFilesUploading(false);
        // on edit expense, show success message after upload completes to make it more clear that files were saved
        if (
          expenseIdRef.current &&
          result?.successful &&
          result.successful.length > 0
        ) {
          showSuccess({
            message: t('fileAttachment.AddNewFilesOnEditSuccessMessage', {
              fileLength: result.successful.length,
            }),
            autoHideDuration: 6000,
          });
        }
        uppyInstance.clear();
      });
      uppyInstance.on('upload-success', (file, response) => {
        const attachments = response?.body?.attachments as unknown as object;
        const attachmentEntries = attachments
          ? Object.entries(attachments)
          : [];

        if (attachmentEntries.length === 0) {
          console.error(
            'No attachments returned from upload response:',
            response,
          );
          return;
        }

        if (!file?.data) {
          console.error('Upload success but file data missing');
          return;
        }

        const [responseKey, responseValue] = attachmentEntries[0];

        setFileAttachments((prevFiles) => [
          ...prevFiles,
          {
            file: file.data as File,
            isError: false,
            isUploaded: true,
            databaseUrl: responseKey ?? null,
            signedUrl: (responseValue as string) ?? null,
          },
        ]);

        // Mark that files were uploaded during this session
        filesUploadedRef.current = true;
      });

      uppyInstance.on('upload-error', (file, error, response) => {
        console.error('Uppy upload-error event:', file, error, response);

        const { responseText } = response as {
          responseText?: string;
        };

        if (
          file &&
          responseText &&
          responseText.includes(
            'Failed to attach the file to the expense, because it is not', // Error used for corrupted files
          )
        ) {
          showError({
            message: t('fileAttachment.CorruptedFileError', {
              fileName: file.name,
            }),
          });
        } else {
          showError({
            message:
              file && file.name
                ? t('fileAttachment.UploadFileError', { fileName: file.name })
                : t('fileAttachment.UploadErrorFallback'),
          });
        }
      });

      uppyInstance.on('error', (error) => {
        console.error('Uppy error event:', error);
        showError({
          message: t('fileAttachment.UploadErrorFallback'),
        });
      });

      return uppyInstance;
    });

    // Update XHR plugin endpoint when expenseId changes
    useEffect(() => {
      const xhrPlugin = uppy.getPlugin('XHRUpload');
      if (xhrPlugin) {
        xhrPlugin.setOptions({
          endpoint: getExpenseAttachmentUploadEndpoint(),
        });
      }
    }, [expenseId]);

    // Clear cache on unmount if files were uploaded during this session, destroy uppy instance
    useEffect(() => {
      return () => {
        if (filesUploadedRef.current && expenseIdRef.current) {
          queryClient.removeQueries({
            queryKey: [
              EXPENSE_ATTACHMENTS_KEY,
              expenseIdRef.current,
              expenseType,
            ],
          });
        }
      };
    }, []);

    const handleFilesInputChanged = (newFiles: File[]) => {
      newFiles.forEach((file) => {
        try {
          uppy.addFile({
            name: file.name,
            type: file.type,
            data: file,
          });
        } catch (error) {
          if (
            (error as object)
              ?.toString()
              ?.includes('exceeds maximum allowed size')
          ) {
            const maxSizeMB = Math.round(
              (uppy.opts.restrictions?.maxFileSize || 0) / (1024 * 1024),
            );
            showError({
              message: t('fileAttachment.FileSizeRestrictionError', {
                fileName: file.name,
                maxSizeMB: maxSizeMB,
              }),
            });
          } else if (
            (error as object)
              ?.toString()
              ?.includes(
                `You can only upload: ${SUPPORTED_FILE_ATTACHMENT_TYPES.join(', ')}`,
              )
          ) {
            const readableFileList = SUPPORTED_FILE_ATTACHMENT_TYPES.map(
              (type) => type.split('/')[1],
            ).join(', ');

            showError({
              message: t('fileAttachment.FileTypeRestrictionError', {
                fileName: file.name,
                fileList: readableFileList,
              }),
            });
          } else {
            console.error('Error adding file:', error);
            showError({
              message: t('fileAttachment.UploadFileError', {
                fileName: file.name,
              }),
            });
          }
        }
      });
    };

    useEffect(() => {
      if (onAttachedExpenseFilesChanged) {
        onAttachedExpenseFilesChanged(
          fileAttachments.map((f) => f.databaseUrl ?? ''),
        );
      }
    }, [fileAttachments]);

    const handleFileAttachmentDeleted = (deletedFileUrl: string) => {
      setFileAttachments((prevFiles) =>
        prevFiles.filter((file) => file.databaseUrl !== deletedFileUrl),
      );
    };

    return (
      <div className={styles.ExpenseFileAttachmentsWrapper}>
        <UppyContextProvider uppy={uppy}>
          <div className={styles.DropzoneWrapper}>
            <FileUploadDropzone
              onFilesChanged={handleFilesInputChanged}
              hideDropzoneUntilDrag={
                (existingExpenseFileUrls &&
                  Object.keys(existingExpenseFileUrls).length > 0) ||
                fileAttachments.length > 0 ||
                !!isExpenseLoading ||
                (!!isExistingFileBlobsLoading && !isExpenseFilesUrlsRefetching)
              }
              disabled={isExpenseLocked}
              disabledHelperText={t('fileAttachment.LockedExpenseHelperText')}
              dropzoneHelperText={t('fileAttachment.DropzoneHelperText')}
            />
          </div>

          {!!isExpenseLoading ||
          (!!isExistingFileBlobsLoading && !isExpenseFilesUrlsRefetching) ? (
            <Stack
              className={styles.LoadingStateWrapper}
              alignItems="center"
              justifyContent="center"
            >
              <CircularProgress color="inherit" thickness={4} />
              <Typography
                className={sx({
                  [styles.LoadingTextHidden]: isExpenseLoading,
                })}
                variant="bodySemiBold"
                color="textSecondary"
              >
                {t('fileAttachment.FilesLoadingHelperText', {
                  fileLength: existingExpenseFileUrls
                    ? Object.keys(existingExpenseFileUrls).length
                    : 0,
                })}
              </Typography>
            </Stack>
          ) : (
            <AttachmentFilePreviewer
              fileAttachments={fileAttachments}
              onFileAttachmentDeleted={handleFileAttachmentDeleted}
              expenseId={expenseId ?? null}
              expenseType={expenseType}
              isExpenseLocked={isExpenseLocked ?? false}
              onAdditionalFilesAdded={handleFilesInputChanged}
            />
          )}
        </UppyContextProvider>
      </div>
    );
  },
);

ExpenseFileAttachments.displayName = 'ExpenseFileAttachments';
